import React, { useEffect, useMemo, useState } from "react";
import { GoDotFill } from "react-icons/go";
import {
  MdTrendingUp, MdTrendingDown, MdShoppingCart, MdEmojiEvents,
  MdCancel, MdAccountBalanceWallet, MdStorefront, MdBarChart,
  MdCalendarToday, MdPeople, MdRefresh, MdSportsEsports,
} from "react-icons/md";
import { RiMoneyRupeeCircleLine } from "react-icons/ri";
import DashboardServices from "../../../services/DashboardServices";
import OutletUsersServices from "../../../services/OutletUsersServices";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

const todayStr = () => new Date().toISOString().slice(0, 10);

// ── Sub-components ────────────────────────────────────────────────────────────

/** Single stat card — top metrics row */
function TopCard({ icon, label, value, accent, loading }) {
  return (
    <div className="adm-top-card" style={{ "--adm-accent": accent }}>
      <div className="adm-top-card-icon">{icon}</div>
      <div className="adm-top-card-body">
        <div className="adm-top-card-label">{label}</div>
        <div className="adm-top-card-value">
          {loading ? <span className="adm-skeleton" /> : value}
        </div>
      </div>
    </div>
  );
}

/** Finance stat card */
function FinCard({ icon, label, value, accent, bg, border, prefix, loading }) {
  return (
    <div className="adm-fin-card" style={{ background: bg, borderColor: border }}>
      <div className="adm-fin-card-top">
        <div className="adm-fin-icon" style={{ background: accent + "22", color: accent }}>
          {icon}
        </div>
        <span className="adm-fin-label">{label}</span>
      </div>
      <div className="adm-fin-value" style={{ color: accent }}>
        {loading ? (
          <span className="adm-skeleton adm-skeleton--wide" />
        ) : (
          <>
            {prefix && <span className="adm-fin-prefix">{prefix}</span>}
            {value}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const DashboardAdmin = ({ user, data }) => {
  const stats = data?.stats || {};
  const wallet = data?.wallet;
  const walletBalance = Number(wallet?.wallet_balance || 0);
  const initialAdminReport = data?.adminReport || null;

  const today = useMemo(() => todayStr(), []);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [storeId, setStoreId] = useState("");
  const [shops, setShops] = useState([]);
  const [adminReport, setAdminReport] = useState(initialAdminReport);
  const [loadingReport, setLoadingReport] = useState(false);

  // Game status is one row per configured game now, straight from the
  // dashboard payload. It used to be two booleans kept in local state and
  // patched over a socket, which could only ever describe 3min and 5min.
  // The container refetches on `gamesChanged`, so this stays current.
  const games = data?.games || [];
  const liveCount = games.filter((g) => g.is_active).length;

  const isManager = [2, 5].includes(Number(user?.role_id));
  const subAdmins = data?.subAdmins || null;

  // Load outlets list
  useEffect(() => {
    if (!isManager) return;
    const api = new OutletUsersServices();
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAllOutletuserData(1, 1000, "");
        if (!cancelled) setShops(res?.data || []);
      } catch { if (!cancelled) setShops([]); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role_id]);

  // Fetch financial summary
  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    (async () => {
      setLoadingReport(true);
      try {
        const res = await DashboardServices.getAdminReportSummaryByFilter({
          fromDate, toDate, storeId: storeId || undefined,
        });
        if (!cancelled && res?.isSuccess) setAdminReport(res?.data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoadingReport(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role_id, fromDate, toDate, storeId]);

  const isProfit = Number(adminReport?.game_profit ?? 0) >= 0;

  // Finance cards config
  const finCards = [
    {
      key: "add_balance", label: "Add Balance",
      icon: <MdTrendingUp size={18} />,
      value: fmt(adminReport?.add_balance),
      accent: "#22c55e", bg: "rgba(34,197,94,0.07)", border: "rgba(34,197,94,0.22)",
    },
    {
      key: "deduct_balance", label: "Deduct Balance",
      icon: <MdTrendingDown size={18} />,
      value: fmt(adminReport?.deduct_balance),
      accent: "#f87171", bg: "rgba(248,113,113,0.07)", border: "rgba(248,113,113,0.22)",
    },
    {
      key: "purchase", label: "Purchase",
      icon: <MdShoppingCart size={18} />,
      value: fmt(adminReport?.purchase),
      accent: "#60a5fa", bg: "rgba(96,165,250,0.07)", border: "rgba(96,165,250,0.22)",
    },
    {
      key: "win", label: "Win",
      icon: <MdEmojiEvents size={18} />,
      value: fmt(adminReport?.win),
      accent: "#fbbf24", bg: "rgba(251,191,36,0.07)", border: "rgba(251,191,36,0.22)",
    },
    {
      key: "game_profit", label: "Game Profit",
      icon: <RiMoneyRupeeCircleLine size={18} />,
      value: fmt(Math.abs(adminReport?.game_profit ?? 0)),
      prefix: isProfit ? "+" : "−",
      accent: isProfit ? "#22c55e" : "#f87171",
      bg: isProfit ? "rgba(34,197,94,0.07)" : "rgba(248,113,113,0.07)",
      border: isProfit ? "rgba(34,197,94,0.22)" : "rgba(248,113,113,0.22)",
    },
    {
      key: "cancel", label: "Total Cancel",
      icon: <MdCancel size={18} />,
      value: fmt(adminReport?.cancel),
      accent: "#c084fc", bg: "rgba(192,132,252,0.07)", border: "rgba(192,132,252,0.22)",
    },
  ];

  return (
    <div className="adm-dashboard">

      {/* ── Row 1: Top metric cards ── */}
      <div className="adm-top-grid">
        <TopCard
          icon={<MdAccountBalanceWallet size={22} />}
          label="Wallet Balance"
          value={fmt(walletBalance)}
          accent="#6366f1"
        />
        <TopCard
          icon={<MdBarChart size={22} />}
          label="Today Sessions"
          value={fmtNum(stats.todaySessionsCount)}
          accent="#6366f1"
        />
        <TopCard
          icon={<MdEmojiEvents size={22} />}
          label="Today Winners"
          value={fmtNum(stats.todayWinnersCount)}
          accent="#f97316"
        />
        {/* Admin only: the sub admins under them and the money parked in their wallets. */}
        {subAdmins && (
          <div className="adm-top-card" style={{ "--adm-accent": "#d97706" }}>
            <div className="adm-top-card-icon"><MdPeople size={22} /></div>
            <div className="adm-top-card-body">
              <div className="adm-top-card-label">Sub Admins</div>
              <div className="adm-top-card-value">
                {fmtNum(subAdmins.active)}
                <span className="adm-top-card-of">/ {fmtNum(subAdmins.total)} active</span>
              </div>
              <div className="adm-top-card-sub">Wallets: {fmt(subAdmins.wallet_total)}</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Row 2: Financial Summary ── */}
      <div className="adm-section">

        {/* Section header */}
        <div className="adm-section-head">
          <div className="adm-section-title-row">
            <MdAccountBalanceWallet size={18} className="adm-section-icon" />
            <h2 className="adm-section-title">Financial Summary</h2>
            {loadingReport && <span className="adm-spin-dot" />}
          </div>

          {/* Filters */}
          <div className="adm-filter-bar">
            <div className="adm-filter-group">
              <MdCalendarToday size={13} className="adm-filter-cal" />
              <label className="adm-filter-label">From</label>
              <input
                type="date"
                className="adm-date-input"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <span className="adm-filter-sep">→</span>
            <div className="adm-filter-group">
              <label className="adm-filter-label">To</label>
              <input
                type="date"
                className="adm-date-input"
                value={toDate}
                min={fromDate}
                max={todayStr()}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="adm-filter-group">
              <MdStorefront size={13} className="adm-filter-cal" />
              <select
                className="adm-select"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
              >
                <option value="">All Shops</option>
                {shops.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name} ({s.username})
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="adm-refresh-btn"
              title="Reset to today"
              onClick={() => { setFromDate(todayStr()); setToDate(todayStr()); setStoreId(""); }}
            >
              <MdRefresh size={15} />
            </button>
          </div>
        </div>

        {/* Finance cards 3+3 grid */}
        <div className="adm-fin-grid">
          {finCards.map((c) => (
            <FinCard key={c.key} loading={loadingReport} {...c} />
          ))}
        </div>
      </div>

      {/* ── Row 3: Games (no amounts - those are Super Admin only) ── */}
      <div className="adm-section">
        <div className="adm-section-head">
          <div className="adm-section-title-row">
            <MdSportsEsports size={18} className="adm-section-icon" />
            <h2 className="adm-section-title">Games</h2>
            <span className="sa-count-pill">
              {liveCount} live · {games.length - liveCount} stopped
            </span>
          </div>
        </div>

        {games.length === 0 ? (
          <div className="sa-empty-state">
            <span className="sa-empty-icon">🎮</span>
            <p>No games configured.</p>
          </div>
        ) : (
          <div className="sa-winners-table-wrap">
            <table className="sa-winners-table sa-games-table">
              <thead>
                <tr>
                  <th>Game</th>
                  <th>Status</th>
                  <th className="sa-num">Duration</th>
                  <th className="sa-num">Ghar</th>
                  <th className="sa-num">Rounds / day</th>
                </tr>
              </thead>
              <tbody>
                {games.map((g) => {
                  const live = Boolean(g.is_active);
                  return (
                    <tr key={g.id} className={live ? "" : "sa-row--muted"}>
                      <td>
                        <div className="sa-game-cell">
                          <span className="sa-game-name">{g.game_name}</span>
                          <span className="sa-game-chip">{g.game_code}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`adm-session-badge ${live ? "adm-badge--live" : "adm-badge--stop"}`}>
                          <GoDotFill size={9} />
                          {live ? "Live" : "Stopped"}
                        </span>
                      </td>
                      <td className="sa-num">{g.duration_minutes} min</td>
                      <td className="sa-num">{g.total_ghar}</td>
                      <td className="sa-num">{g.sessions_per_day}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardAdmin;
